import React, { useState, useRef, useCallback, useEffect } from 'react';
import { collection, addDoc, doc, updateDoc, arrayUnion, arrayRemove, onSnapshot, query, where, deleteDoc, getDoc } from 'firebase/firestore';
import { db, auth } from '../../Firebaseconfig'; 

/* ─── tiny shared styles ─────────────────────────────────────────────────── */
const S = {
  card: {
    background: '#1e2126',
    border: '1px solid rgba(255,255,255,0.07)',
    borderRadius: 10,
    padding: '20px 22px',
  },
  sectionHead: {
    display: 'flex', alignItems: 'center', gap: 8,
    fontSize: 13.5, fontWeight: 600, color: '#e2e4f3',
    marginBottom: 18,
  },
  sectionIcon: {
    width: 26, height: 26, borderRadius: 7,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 13,
  },
  label: {
    fontSize: 11.5, fontWeight: 500, color: '#6b7280',
    marginBottom: 6, display: 'block', letterSpacing: '0.01em',
  },
  input: {
    width: '100%', height: 38,
    background: '#1a1a1a',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 7, padding: '0 12px',
    color: '#e2e4f3', fontSize: 13,
    fontFamily: 'Inter, sans-serif',
    outline: 'none', transition: 'border-color 0.15s',
  },
  textarea: {
    width: '100%', minHeight: 86,
    background: '#1a1a1a',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 7, padding: '10px 12px',
    color: '#e2e4f3', fontSize: 13,
    fontFamily: 'Inter, sans-serif',
    outline: 'none', resize: 'vertical',
    transition: 'border-color 0.15s',
  },
  select: {
    width: '100%', height: 38,
    background: '#1a1a1a',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 7, padding: '0 12px',
    color: '#e2e4f3', fontSize: 13,
    fontFamily: 'Inter, sans-serif',
    outline: 'none', cursor: 'pointer',
    appearance: 'none', transition: 'border-color 0.15s',
  },
  fieldWrap: { marginBottom: 14 },
};

function focusBorder(e) { e.target.style.borderColor = 'rgba(249,115,22,0.6)'; }
function blurBorder(e)  { e.target.style.borderColor = 'rgba(255,255,255,0.08)'; }

/* ─── Toggle Switch ──────────────────────────────────────────────────────── */
function Toggle({ checked, onChange }) {
  const bg = checked ? '#f97316' : '#2a2a2a';
  return (
    <div
      onClick={() => onChange(!checked)}
      style={{
        width: 40, height: 22, borderRadius: 999,
        background: bg, position: 'relative', cursor: 'pointer',
        transition: 'background 0.2s', flexShrink: 0,
        border: '1px solid rgba(255,255,255,0.08)',
      }}
    >
      <div style={{
        position: 'absolute',
        top: 2, left: checked ? 20 : 2,
        width: 16, height: 16, borderRadius: '50%',
        background: '#fff', transition: 'left 0.2s',
        boxShadow: '0 1px 3px rgba(0,0,0,0.4)',
      }}/>
    </div>
  );
}

/* ─── Image Upload Zone ──────────────────────────────────────────────────── */
function ImageUploadZone({ images, onAdd, onRemove }) {
  const inputRef = useRef();
  const [dragging, setDragging] = useState(false);

  function handleFiles(files) {
    Array.from(files).forEach(f => {
      if (!f.type.startsWith('image/')) return;
      const url = URL.createObjectURL(f);
      onAdd({ file: f, preview: url, name: f.name });
    });
  }

  const onDrop = useCallback(e => {
    e.preventDefault(); setDragging(false);
    handleFiles(e.dataTransfer.files);
  }, []);

  return (
    <div>
      {images.length === 0 && (
        <div
          onDragOver={e => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          onClick={() => inputRef.current.click()}
          style={{
            border: `1.5px dashed ${dragging ? '#f97316' : 'rgba(255,255,255,0.12)'}`,
            borderRadius: 10, padding: '32px 20px',
            display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center', gap: 8,
            cursor: 'pointer', background: dragging ? 'rgba(249,115,22,0.06)' : 'rgba(255,255,255,0.02)',
            transition: 'all 0.15s', marginBottom: 12,
          }}
        >
          <div style={{
            width: 44, height: 44, borderRadius: 999,
            background: 'rgba(255,255,255,0.06)',
            border: '1px solid rgba(255,255,255,0.12)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 20,
          }}>☁</div>
          <div style={{ fontSize: 12.5, color: '#e2e4f3', fontWeight: 500 }}>
            Click to upload or drag and drop
          </div>
          <div style={{ fontSize: 11, color: '#4b5563' }}>
            PNG, JPG or WebP (1800×1200px recommended)
          </div>
        </div>
      )}

      {images.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
          {images.map((img, i) => (
            <div key={i} style={{ position: 'relative', width: 72, height: 72 }}>
              <img
                src={img.preview}
                alt={img.name}
                style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 8, border: '1px solid rgba(255,255,255,0.1)' }}
              />
              <button
                onClick={() => onRemove(i)}
                style={{
                  position: 'absolute', top: -6, right: -6,
                  width: 18, height: 18, borderRadius: '50%',
                  background: '#ef4444', border: 'none',
                  color: '#fff', fontSize: 11, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  lineHeight: 1,
                }}
              >×</button>
            </div>
          ))}
          <div
            onClick={() => inputRef.current.click()}
            style={{
              width: 72, height: 72, borderRadius: 8,
              border: '1.5px dashed rgba(255,255,255,0.12)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', color: '#4b5563', fontSize: 22,
              background: 'rgba(255,255,255,0.02)',
            }}
          >+</div>
        </div>
      )}

      <input
        ref={inputRef} type="file" multiple accept="image/*"
        style={{ display: 'none' }}
        onChange={e => handleFiles(e.target.files)}
      />
    </div>
  );
}

/* ─── MAIN COMPONENT ─────────────────────────────────────────────────────── */
const DEFAULT_CATEGORIES = ['Burgers', 'Pizza', 'Tacos', 'Salads', 'Drinks', 'Desserts', 'Sides', 'Breakfast'];

const EMPTY_FORM = {
  name: '', description: '',
  basePrice: '', discount: '',
  category: '', inventoryStatus: 'instock',
  onlineStore: true, featuredProduct: false,
  sizes: [], extras: [],
};

export default function ProductBar({ onBack }) {
  const [view, setView] = useState('list'); // 'list', 'form', or 'categories'
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(true);
  const [editingProduct, setEditingProduct] = useState(null);

  const [form, setForm] = useState(EMPTY_FORM);
  const [images,  setImages]  = useState([]);
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState(null);
  const [success, setSuccess] = useState(false);

  const [menuCategories, setMenuCategories] = useState(DEFAULT_CATEGORIES);
  const [newCat, setNewCat] = useState('');
  const [addingCat, setAddingCat] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const finalPrice = form.basePrice
    ? (parseFloat(form.basePrice || 0) * (1 - parseFloat(form.discount || 0) / 100)).toFixed(2)
    : '0.00';

  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;
    
    // Fetch custom categories
    const userRef = doc(db, 'users', user.uid);
    const unsubUser = onSnapshot(userRef, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        if (data.menuCategories !== undefined && Array.isArray(data.menuCategories)) {
          setMenuCategories(data.menuCategories);
        } else {
          setMenuCategories(DEFAULT_CATEGORIES);
          updateDoc(userRef, { menuCategories: DEFAULT_CATEGORIES }).catch(console.error);
        }
      } else {
        setMenuCategories(DEFAULT_CATEGORIES);
      }
    });

    // Fetch products
    const q = query(collection(db, 'products'), where('postedBy', '==', user.uid));
    const unsubProducts = onSnapshot(q, (snap) => {
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setProducts(data);
      setProductsLoading(false);
    });

    return () => {
      unsubUser();
      unsubProducts();
    };
  }, []);

  async function handleAddCategory() {
    const cat = newCat.trim();
    if (!cat || menuCategories.includes(cat)) {
      setAddingCat(false);
      return;
    }
    try {
      const userRef = doc(db, 'users', auth.currentUser.uid);
      await updateDoc(userRef, {
        menuCategories: arrayUnion(cat)
      });
      set('category', cat);
      setNewCat('');
      setAddingCat(false);
    } catch (err) {
      console.error(err);
    }
  }

  async function handleRemoveCategory(catToRemove) {
    if (!window.confirm(`Delete the custom category "${catToRemove}"?`)) return;
    try {
      const userRef = doc(db, 'users', auth.currentUser.uid);
      await updateDoc(userRef, {
        menuCategories: arrayRemove(catToRemove)
      });
      if (form.category === catToRemove) {
        set('category', '');
      }
    } catch (err) {
      console.error(err);
    }
  }

  async function handleAddCategoryFromManager() {
    const cat = newCat.trim();
    if (!cat || menuCategories.includes(cat)) return;
    try {
      const userRef = doc(db, 'users', auth.currentUser.uid);
      await updateDoc(userRef, {
        menuCategories: arrayUnion(cat)
      });
      setNewCat('');
    } catch (err) {
      console.error(err);
    }
  }

  async function handleDeleteCategoryFromManager(catToRemove) {
    if (!window.confirm(`Delete the category "${catToRemove}"? Products using it will remain but won't have a category.`)) return;
    try {
      const userRef = doc(db, 'users', auth.currentUser.uid);
      await updateDoc(userRef, {
        menuCategories: arrayRemove(catToRemove)
      });
    } catch (err) {
      console.error(err);
    }
  }

  async function handleDuplicateCategory(cat) {
    const newName = `${cat} (Copy)`;
    if (menuCategories.includes(newName)) {
      alert("A copy already exists. Please rename it first.");
      return;
    }
    try {
      const userRef = doc(db, 'users', auth.currentUser.uid);
      await updateDoc(userRef, {
        menuCategories: arrayUnion(newName)
      });
    } catch (err) {
      console.error(err);
    }
  }

  async function handleEditCategoryName(oldCat) {
    const newCatPrompt = window.prompt(`Rename category "${oldCat}" to:`, oldCat);
    if (!newCatPrompt) return;
    const newName = newCatPrompt.trim();
    if (!newName || newName === oldCat) return;
    if (menuCategories.includes(newName)) {
      alert("A category with this name already exists.");
      return;
    }

    try {
      const user = auth.currentUser;
      const userRef = doc(db, 'users', user.uid);
      
      const updatedCats = menuCategories.map(c => c === oldCat ? newName : c);
      await updateDoc(userRef, { menuCategories: updatedCats });

      const affectedProducts = products.filter(p => p.category === oldCat);
      const updatePromises = affectedProducts.map(p => {
        const pRef = doc(db, 'products', p.id);
        return updateDoc(pRef, { category: newName });
      });
      await Promise.all(updatePromises);

      const userSnap = await getDoc(userRef);
      if (userSnap.exists()) {
        const userData = userSnap.data();
        const updatedUserProducts = (userData.products || []).map(p => 
          p.category === oldCat ? { ...p, category: newName } : p
        );
        await updateDoc(userRef, { products: updatedUserProducts });
      }

      alert(`Successfully renamed "${oldCat}" to "${newName}" and updated ${affectedProducts.length} product(s).`);

    } catch (err) {
      console.error(err);
      alert("An error occurred while renaming the category.");
    }
  }

  /* ── Convert image file to base64 data URI ── */
  async function toBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  function handleAddNew() {
    setForm(EMPTY_FORM);
    setImages([]);
    setEditingProduct(null);
    setError(null);
    setView('form');
  }

  function handleEdit(product) {
    setForm({
      name: product.name || '', description: product.description || '',
      basePrice: product.basePrice || '', discount: product.discount || '',
      category: product.category || '', inventoryStatus: product.inventoryStatus || 'instock',
      onlineStore: product.onlineStore ?? true, featuredProduct: product.featuredProduct ?? false,
      sizes: product.sizes || [], extras: product.extras || [],
    });
    setImages(
      (product.images || []).map((url, i) => ({ preview: url, file: null, name: `image-${i}` }))
    );
    setEditingProduct(product);
    setError(null);
    setView('form');
  }

  async function handleDuplicate(product) {
    if (!window.confirm(`Duplicate "${product.name}"?`)) return;
    try {
      const user = auth.currentUser;
      const dup = { ...product };
      delete dup.id;
      dup.name = `${dup.name} (Copy)`;
      dup.createdAt = new Date().toISOString();
      
      const docRef = await addDoc(collection(db, 'products'), dup);
      
      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, {
        products: arrayUnion({ ...dup, id: docRef.id })
      });
    } catch (err) {
      console.error("Duplicate error", err);
      alert("Failed to duplicate product.");
    }
  }

  async function handleDelete(product) {
    if (!window.confirm(`Are you sure you want to delete "${product.name}"?`)) return;
    try {
      const user = auth.currentUser;
      await deleteDoc(doc(db, 'products', product.id));
      
      const userRef = doc(db, 'users', user.uid);
      const userSnap = await getDoc(userRef);
      if (userSnap.exists()) {
        const userData = userSnap.data();
        const updatedProducts = (userData.products || []).filter(p => p.id !== product.id);
        await updateDoc(userRef, { products: updatedProducts });
      }
    } catch (err) {
      console.error("Delete error", err);
      alert("Failed to delete product.");
    }
  }

  /* ── Save handler ── */
  async function handleSave() {
    if (!form.name.trim()) { setError('Product name is required.'); return; }

    setError(null);
    setSaving(true);

    try {
      const user = auth.currentUser;
      if (!user) {
        setError('You must be signed in to save a product.');
        setSaving(false);
        return;
      }

      let imageUrls = [];
      if (images.length > 0) {
        try {
          imageUrls = await Promise.all(
            images.map(img => img.file ? toBase64(img.file) : img.preview)
          );
        } catch (uploadErr) {
          setError('Failed to process images. Try smaller files.');
          setSaving(false);
          return;
        }
      }

      const productData = {
        name:              form.name.trim(),
        description:       form.description.trim(),
        basePrice:         parseFloat(form.basePrice) || 0,
        discount:          parseFloat(form.discount)  || 0,
        finalPrice:        parseFloat(finalPrice),
        category:          form.category,
        inventoryStatus:   form.inventoryStatus,
        onlineStore:       form.onlineStore,
        featuredProduct:   form.featuredProduct,
        images:            imageUrls,
        createdAt:         editingProduct ? editingProduct.createdAt : new Date().toISOString(),
        postedBy:          user.uid,
        posterEmail:       user.email,
        posterDisplayName: user.displayName || user.email,
        sizes:             form.sizes || [],
        extras:            form.extras || [],
      };

      const userRef = doc(db, 'users', user.uid);

      if (editingProduct) {
        // Update global
        const productRef = doc(db, 'products', editingProduct.id);
        await updateDoc(productRef, productData);
        
        // Update in user profile array
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          const userData = userSnap.data();
          const updatedProducts = (userData.products || []).map(p => 
            p.id === editingProduct.id ? { ...productData, id: editingProduct.id } : p
          );
          await updateDoc(userRef, { products: updatedProducts });
        }
      } else {
        // Create global
        const docRef = await addDoc(collection(db, 'products'), productData);
        
        // Add to user profile array
        await updateDoc(userRef, {
          products: arrayUnion({ ...productData, id: docRef.id })
        });
      }

      setSuccess(true);
      setTimeout(() => { 
        setSuccess(false); 
        setView('list'); 
      }, 1000);

    } catch (e) {
      console.error('[ProductBar] Firestore save error:', e);
      if (e?.code === 'permission-denied') {
        setError('Firestore blocked — add a /products rule to your Firestore security rules.');
      } else {
        setError(`Save failed: ${e?.message || 'Unknown error'}`);
      }
    } finally {
      setSaving(false);
    }
  }

  /* ─────────────────────────────────────────────────────────────────────── */

  if (view === 'categories') {
    return (
      <div style={{ fontFamily: 'Inter, sans-serif', color: '#e2e4f3', width: '100%' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 22 }}>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 700, color: '#e2e4f3', letterSpacing: '-0.01em', lineHeight: 1.1 }}>
              Manage Categories
            </h1>
            <p style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
              Add, rename, duplicate, or remove categories for your menu.
            </p>
          </div>
          <button
            onClick={() => setView('list')}
            style={{
              height: 34, padding: '0 16px', borderRadius: 7,
              background: '#2a2a2a', border: '1px solid rgba(255,255,255,0.1)', color: '#fff',
              fontSize: 12.5, fontWeight: 600, cursor: 'pointer',
            }}
          >
            ← Back to Products
          </button>
        </div>

        <div style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
          <input
            style={{ ...S.input, maxWidth: 300 }}
            placeholder="New Category Name..."
            value={newCat}
            onChange={e => setNewCat(e.target.value)}
          />
          <button 
            onClick={handleAddCategoryFromManager} 
            style={{ padding: '0 16px', borderRadius: 7, background: '#f97316', color: '#fff', fontSize: 13, fontWeight: 600, border: 'none', cursor: 'pointer' }}
          >
            Add Category
          </button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {menuCategories.map(cat => (
            <div key={cat} style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              background: '#1e2126', border: '1px solid rgba(255,255,255,0.07)',
              borderRadius: 10, padding: '12px 16px',
            }}>
              <div style={{ fontSize: 15, fontWeight: 600, color: '#e2e4f3' }}>
                {cat}
              </div>
              
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => handleEditCategoryName(cat)} style={{ background: '#2a2a2a', border: '1px solid rgba(255,255,255,0.1)', color: '#e2e4f3', padding: '6px 12px', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>Edit</button>
                <button onClick={() => handleDuplicateCategory(cat)} style={{ background: '#2a2a2a', border: '1px solid rgba(255,255,255,0.1)', color: '#e2e4f3', padding: '6px 12px', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>Duplicate</button>
                <button onClick={() => handleDeleteCategoryFromManager(cat)} style={{ background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.2)', color: '#ef4444', padding: '6px 12px', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>Delete</button>
              </div>
            </div>
          ))}
          {menuCategories.length === 0 && (
            <div style={{ padding: 40, textAlign: 'center', color: '#6b7280', fontSize: 13, border: '1px dashed rgba(255,255,255,0.1)', borderRadius: 10 }}>
              No categories found. Start by adding one above.
            </div>
          )}
        </div>
      </div>
    );
  }

  if (view === 'list') {
    return (
      <div style={{ fontFamily: 'Inter, sans-serif', color: '#e2e4f3', width: '100%' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 22 }}>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 700, color: '#e2e4f3', letterSpacing: '-0.01em', lineHeight: 1.1 }}>
              Menu Products
            </h1>
            <p style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
              Manage your existing products or create new ones.
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={() => setView('categories')}
              style={{
                height: 34, padding: '0 16px', borderRadius: 7,
                background: '#2a2a2a', border: '1px solid rgba(255,255,255,0.1)', color: '#fff',
                fontSize: 12.5, fontWeight: 600, cursor: 'pointer',
              }}
            >
              Manage Categories
            </button>
            <button
              onClick={handleAddNew}
              style={{
                height: 34, padding: '0 16px', borderRadius: 7,
                background: '#f97316', border: 'none', color: '#fff',
                fontSize: 12.5, fontWeight: 700, cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 7, transition: 'background 0.15s',
              }}
            >
              + Add Product
            </button>
          </div>
        </div>

        {productsLoading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#6b7280', fontSize: 13 }}>Loading products...</div>
        ) : products.length === 0 ? (
          <div style={{ padding: 60, textAlign: 'center', background: '#1e2126', borderRadius: 12, border: '1px dashed rgba(255,255,255,0.1)' }}>
            <div style={{ fontSize: 32, marginBottom: 12 }}>🍔</div>
            <h3 style={{ fontSize: 16, color: '#e2e4f3', marginBottom: 6 }}>No Products Yet</h3>
            <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 20 }}>Your menu is empty. Start by adding your first product.</p>
            <button
              onClick={handleAddNew}
              style={{ padding: '8px 20px', borderRadius: 7, background: '#f97316', color: '#fff', fontSize: 13, fontWeight: 600, border: 'none', cursor: 'pointer' }}
            >
              Create Product
            </button>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {products.map(p => (
              <div key={p.id} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                background: '#1e2126', border: '1px solid rgba(255,255,255,0.07)',
                borderRadius: 10, padding: '12px 16px',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                  <img 
                    src={p.images?.[0] || 'https://via.placeholder.com/60?text=No+Img'} 
                    alt={p.name} 
                    style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 8, border: '1px solid rgba(255,255,255,0.1)' }} 
                  />
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: '#e2e4f3', marginBottom: 4 }}>{p.name}</div>
                    <div style={{ fontSize: 12, color: '#94a3b8', display: 'flex', gap: 12, alignItems: 'center' }}>
                      <span style={{ color: '#f97316', fontWeight: 600 }}>€{(p.finalPrice || 0).toFixed(2)}</span>
                      <span>•</span>
                      <span>{p.category || 'Uncategorized'}</span>
                      <span>•</span>
                      <span style={{ color: p.inventoryStatus === 'instock' ? '#4ade80' : '#f87171' }}>
                        {p.inventoryStatus === 'instock' ? 'In Stock' : 'Out of Stock'}
                      </span>
                    </div>
                  </div>
                </div>
                
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => handleEdit(p)} style={{ background: '#2a2a2a', border: '1px solid rgba(255,255,255,0.1)', color: '#e2e4f3', padding: '6px 12px', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>Edit</button>
                  <button onClick={() => handleDuplicate(p)} style={{ background: '#2a2a2a', border: '1px solid rgba(255,255,255,0.1)', color: '#e2e4f3', padding: '6px 12px', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>Duplicate</button>
                  <button onClick={() => handleDelete(p)} style={{ background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.2)', color: '#ef4444', padding: '6px 12px', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>Delete</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={{ fontFamily: 'Inter, sans-serif', color: '#e2e4f3', width: '100%' }}>
      <style>{`
        .pb-grid {
          display: grid;
          grid-template-columns: 1fr 280px;
          gap: 16px;
        }
        .pb-header {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          margin-bottom: 22px;
        }
        .pb-header-actions {
          display: flex;
          gap: 8px;
          flex-shrink: 0;
          align-items: center;
        }
        @media (max-width: 768px) {
          .pb-grid {
            grid-template-columns: 1fr;
          }
          .pb-header {
            flex-direction: column;
            gap: 16px;
          }
          .pb-header-actions {
            width: 100%;
            justify-content: stretch;
          }
          .pb-header-actions button {
            flex: 1;
            justify-content: center;
          }
        }
      `}</style>

      {/* ── Back link ── */}
      <div
        onClick={() => setView('list')}
        style={{ fontSize: 12, color: '#6b7280', cursor: 'pointer', marginBottom: 10, display: 'inline-flex', alignItems: 'center', gap: 5 }}
      >
        ← Back to Products
      </div>

      {/* ── Page header ── */}
      <div className="pb-header">
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: '#e2e4f3', letterSpacing: '-0.01em', lineHeight: 1.1 }}>
            {editingProduct ? 'Edit Product' : 'Add New Product'}
          </h1>
          <p style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
            Configure your product details, visibility, and inventory settings.
          </p>
        </div>
        <div className="pb-header-actions">
          {error   && <span style={{ fontSize: 11.5, color: '#f87171', maxWidth: 260 }}>{error}</span>}
          {success && <span style={{ fontSize: 11.5, color: '#4ade80' }}>✓ Saved!</span>}
          <button
            onClick={() => setView('list')}
            style={{
              height: 34, padding: '0 16px', borderRadius: 7,
              background: 'transparent', border: '1px solid rgba(255,255,255,0.1)',
              color: '#94a3b8', fontSize: 12.5, fontWeight: 500, cursor: 'pointer',
            }}
          >Discard</button>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              height: 34, padding: '0 16px', borderRadius: 7,
              background: saving ? '#7c3a00' : '#f97316',
              border: 'none', color: '#fff',
              fontSize: 12.5, fontWeight: 700, cursor: saving ? 'wait' : 'pointer',
              display: 'flex', alignItems: 'center', gap: 7,
              opacity: saving ? 0.8 : 1, transition: 'background 0.15s',
            }}
          >
            {saving ? '⏳ Saving…' : '💾 Save Product'}
          </button>
        </div>
      </div>

      {/* ── Two-column grid ── */}
      <div className="pb-grid">

        {/* ── LEFT COLUMN ── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* General Information */}
          <div style={S.card}>
            <div style={S.sectionHead}>
              <div style={{ ...S.sectionIcon, background: 'rgba(249,115,22,0.12)', border: '1px solid rgba(249,115,22,0.25)' }}>ℹ</div>
              General Information
            </div>

            <div style={S.fieldWrap}>
              <label style={S.label}>Product Name</label>
              <input
                style={S.input}
                placeholder="e.g. Signature Truffle Burger"
                value={form.name}
                onChange={e => set('name', e.target.value)}
                onFocus={focusBorder} onBlur={blurBorder}
              />
            </div>

            <div style={S.fieldWrap}>
              <label style={S.label}>Description</label>
              <textarea
                style={S.textarea}
                placeholder="Describe the flavors, ingredients, and story behind this item…"
                value={form.description}
                onChange={e => set('description', e.target.value)}
                onFocus={focusBorder} onBlur={blurBorder}
              />
            </div>
          </div>

          {/* Product Media */}
          <div style={S.card}>
            <div style={{ ...S.sectionHead, justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ ...S.sectionIcon, background: 'rgba(14,165,233,0.12)', border: '1px solid rgba(14,165,233,0.25)' }}>🖼</div>
                Product Media
              </div>
              <span style={{ fontSize: 11, color: '#4b5563', fontWeight: 400 }}>Max 5MB per image</span>
            </div>

            <ImageUploadZone
              images={images}
              onAdd={img => setImages(prev => [...prev, img])}
              onRemove={i => setImages(prev => prev.filter((_, j) => j !== i))}
            />
          </div>

          {/* Sizes Configuration */}
          <div style={S.card}>
            <div style={{ ...S.sectionHead, justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ ...S.sectionIcon, background: 'rgba(168,85,247,0.12)', border: '1px solid rgba(168,85,247,0.25)' }}>📏</div>
                Sizes Configuration
              </div>
              <span style={{ fontSize: 11, color: '#4b5563', fontWeight: 400 }}>Max 3 sizes</span>
            </div>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {(form.sizes || []).map((sz, idx) => (
                <div key={sz.id || idx} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input 
                    style={{ ...S.input, width: '60px' }} 
                    placeholder="S" 
                    value={sz.label} 
                    onChange={e => {
                      const newSizes = [...form.sizes];
                      newSizes[idx].label = e.target.value;
                      set('sizes', newSizes);
                    }}
                  />
                  <input 
                    style={{ ...S.input, flex: 1 }} 
                    placeholder="Small" 
                    value={sz.name} 
                    onChange={e => {
                      const newSizes = [...form.sizes];
                      newSizes[idx].name = e.target.value;
                      set('sizes', newSizes);
                    }}
                  />
                  <div style={{ position: 'relative', width: '90px' }}>
                    <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#6b7280', fontSize: 13 }}>+$</span>
                    <input 
                      type="number" step="0.01"
                      style={{ ...S.input, paddingLeft: 26 }} 
                      placeholder="0.00" 
                      value={sz.priceOffset} 
                      onChange={e => {
                        const newSizes = [...form.sizes];
                        newSizes[idx].priceOffset = parseFloat(e.target.value) || 0;
                        set('sizes', newSizes);
                      }}
                    />
                  </div>
                  <button 
                    type="button"
                    onClick={() => {
                      const newSizes = form.sizes.filter((_, i) => i !== idx);
                      set('sizes', newSizes);
                    }}
                    style={{ background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', border: 'none', width: 32, height: 32, borderRadius: 6, cursor: 'pointer' }}
                  >×</button>
                </div>
              ))}
              
              {(form.sizes || []).length < 3 && (
                <button 
                  type="button"
                  onClick={() => {
                    const currentCount = (form.sizes || []).length;
                    let defaultLabel = '';
                    let defaultName = '';
                    let defaultOffset = 0;
                    if (currentCount === 0) { defaultLabel = 'S'; defaultName = 'Small'; defaultOffset = 0; }
                    else if (currentCount === 1) { defaultLabel = 'M'; defaultName = 'Medium'; defaultOffset = 2.50; }
                    else if (currentCount === 2) { defaultLabel = 'L'; defaultName = 'Large'; defaultOffset = 5.00; }
                    
                    set('sizes', [...(form.sizes || []), { id: Date.now().toString(), label: defaultLabel, name: defaultName, priceOffset: defaultOffset }]);
                  }}
                  style={{ alignSelf: 'flex-start', background: 'rgba(255,255,255,0.05)', border: '1px dashed rgba(255,255,255,0.2)', color: '#e2e4f3', padding: '8px 16px', borderRadius: 7, cursor: 'pointer', fontSize: 12, marginTop: 4 }}
                >+ Add Size</button>
              )}
            </div>
          </div>

          {/* Extras / Customizations */}
          <div style={S.card}>
            <div style={S.sectionHead}>
              <div style={{ ...S.sectionIcon, background: 'rgba(236,72,153,0.12)', border: '1px solid rgba(236,72,153,0.25)' }}>✨</div>
              Extras & Customizations
            </div>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {(form.extras || []).map((ex, idx) => (
                <div key={ex.id || idx} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input 
                    style={{ ...S.input, flex: 1 }} 
                    placeholder="e.g. Extra Truffle Mayo" 
                    value={ex.label} 
                    onChange={e => {
                      const newExtras = [...form.extras];
                      newExtras[idx].label = e.target.value;
                      set('extras', newExtras);
                    }}
                  />
                  <div style={{ position: 'relative', width: '90px' }}>
                    <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#6b7280', fontSize: 13 }}>+$</span>
                    <input 
                      type="number" step="0.01"
                      style={{ ...S.input, paddingLeft: 26 }} 
                      placeholder="0.00" 
                      value={ex.price} 
                      onChange={e => {
                        const newExtras = [...form.extras];
                        newExtras[idx].price = parseFloat(e.target.value) || 0;
                        set('extras', newExtras);
                      }}
                    />
                  </div>
                  <button 
                    type="button"
                    onClick={() => {
                      const newExtras = form.extras.filter((_, i) => i !== idx);
                      set('extras', newExtras);
                    }}
                    style={{ background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', border: 'none', width: 32, height: 32, borderRadius: 6, cursor: 'pointer' }}
                  >×</button>
                </div>
              ))}
              
              <button 
                type="button"
                onClick={() => set('extras', [...(form.extras || []), { id: Date.now().toString(), label: '', price: 0 }])}
                style={{ alignSelf: 'flex-start', background: 'rgba(255,255,255,0.05)', border: '1px dashed rgba(255,255,255,0.2)', color: '#e2e4f3', padding: '8px 16px', borderRadius: 7, cursor: 'pointer', fontSize: 12, marginTop: 4 }}
              >+ Add Extra</button>
            </div>
          </div>
        </div>

        {/* ── RIGHT COLUMN ── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* Pricing */}
          <div style={S.card}>
            <div style={S.sectionHead}>
              <div style={{ ...S.sectionIcon, background: 'rgba(74,222,128,0.1)', border: '1px solid rgba(74,222,128,0.2)' }}>💲</div>
              Pricing
            </div>

            <div style={S.fieldWrap}>
              <label style={S.label}>Base Price (€)</label>
              <input
                style={S.input} type="number" min="0" step="0.01"
                placeholder="0.00"
                value={form.basePrice}
                onChange={e => set('basePrice', e.target.value)}
                onFocus={focusBorder} onBlur={blurBorder}
              />
            </div>

            <div style={S.fieldWrap}>
              <label style={S.label}>Discount (%)</label>
              <input
                style={S.input} type="number" min="0" max="100" step="1"
                placeholder="0"
                value={form.discount}
                onChange={e => set('discount', e.target.value)}
                onFocus={focusBorder} onBlur={blurBorder}
              />
            </div>

            <div style={{
              background: '#1a1a1a',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: 7, padding: '10px 14px',
            }}>
              <div style={{ fontSize: 10.5, color: '#4b5563', fontWeight: 500, marginBottom: 4 }}>Final Customer Price</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: '#f97316', letterSpacing: '-0.02em' }}>
                €{finalPrice}
              </div>
            </div>
          </div>

          {/* Logistics */}
          <div style={S.card}>
            <div style={S.sectionHead}>
              <div style={{ ...S.sectionIcon, background: 'rgba(251,146,60,0.1)', border: '1px solid rgba(251,146,60,0.2)' }}>📦</div>
              Logistics
            </div>

            <div style={S.fieldWrap}>
              <label style={S.label}>Category</label>
              {addingCat ? (
                <div style={{ display: 'flex', gap: 8 }}>
                  <input
                    style={{ ...S.input, flex: 1 }}
                    placeholder="New category..."
                    value={newCat}
                    onChange={e => setNewCat(e.target.value)}
                    autoFocus
                  />
                  <button onClick={handleAddCategory} style={{ padding: '0 12px', background: '#f97316', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>Add</button>
                  <button onClick={() => setAddingCat(false)} style={{ padding: '0 12px', background: 'transparent', color: '#94a3b8', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 6, cursor: 'pointer', fontSize: 12 }}>X</button>
                </div>
              ) : (
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <div style={{ position: 'relative', flex: 1 }}>
                    <select
                      style={S.select}
                      value={form.category}
                      onChange={e => {
                        if (e.target.value === '__ADD_NEW__') {
                          setAddingCat(true);
                        } else {
                          set('category', e.target.value);
                        }
                      }}
                      onFocus={focusBorder} onBlur={blurBorder}
                    >
                      <option value="">Select a category</option>
                      {menuCategories.map(c => <option key={c} value={c}>{c}</option>)}
                      <option value="__ADD_NEW__" style={{ color: '#f97316', fontWeight: 'bold' }}>+ Add Custom Category</option>
                    </select>
                    <span style={{ position: 'absolute', right: 11, top: '50%', transform: 'translateY(-50%)', color: '#4b5563', fontSize: 11, pointerEvents: 'none' }}>▾</span>
                  </div>
                  {form.category && !DEFAULT_CATEGORIES.includes(form.category) && (
                    <button
                      onClick={() => handleRemoveCategory(form.category)}
                      title="Delete this custom category"
                      style={{
                        padding: '0', width: 38, height: 38, flexShrink: 0,
                        background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.2)',
                        color: '#ef4444', borderRadius: 7, cursor: 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14
                      }}
                    >
                      🗑️
                    </button>
                  )}
                </div>
              )}
            </div>

            <div>
              <label style={S.label}>Inventory Status</label>
              <div style={{ display: 'flex', gap: 8 }}>
                {['instock', 'outofstock'].map(val => {
                  const active = form.inventoryStatus === val;
                  const label  = val === 'instock' ? 'In Stock' : 'Out of Stock';
                  return (
                    <button
                      key={val}
                      onClick={() => set('inventoryStatus', val)}
                      style={{
                        flex: 1, height: 32, borderRadius: 7,
                        border: active ? 'none' : '1px solid rgba(255,255,255,0.08)',
                        background: active
                          ? (val === 'instock' ? '#f97316' : '#374151')
                          : 'transparent',
                        color: active ? '#fff' : '#6b7280',
                        fontSize: 12, fontWeight: 600, cursor: 'pointer',
                        transition: 'all 0.15s',
                      }}
                    >{label}</button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Visibility */}
          <div style={S.card}>
            <div style={S.sectionHead}>
              <div style={{ ...S.sectionIcon, background: 'rgba(249,115,22,0.1)', border: '1px solid rgba(249,115,22,0.2)' }}>👁</div>
              Visibility
            </div>

            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 16, gap: 12 }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 500, color: '#e2e4f3', marginBottom: 3 }}>Online Store</div>
                <div style={{ fontSize: 11, color: '#4b5563' }}>Product will be visible to all customers.</div>
              </div>
              <Toggle checked={form.onlineStore} onChange={v => set('onlineStore', v)}/>
            </div>

            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 500, color: '#e2e4f3', marginBottom: 3 }}>Featured Product</div>
                <div style={{ fontSize: 11, color: '#4b5563' }}>Show at the top of your menu.</div>
              </div>
              <Toggle checked={form.featuredProduct} onChange={v => set('featuredProduct', v)}/>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
